// App Bootstrap
import { getActionCreatorProviders, getAsyncDispatcher, getAuthServiceProvider, getContextAndInitializeCultureData, getInitialRoute, getLogicModuleProviders, getRequestPrioritizer, getServiceProviders, getStoreProviders, getTelemetryContext, getWorkerProviders, loadAndInitializeWorkers, logPlanViewSubRouteType, setupAppContext, } from "../utilities/AppBootstrapUtils";
// Constants
import { AppRootElementId, Environment } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
// Controls
import { applyUnoGlobalStyleOverrides } from "@ms/uno-shell/lib/local/controls/unoShell/Global.styles";
import { UnoShell } from "@ms/uno-shell/lib/local/controls/unoShell/UnoShell";
import { ProductionServiceOrchestrator } from "../base/factories/ProductionServiceOrchestrator";
import { TeamsActionCreatorFactory } from "./factories/TeamsActionCreatorFactory";
import { TeamsLogicModuleFactory } from "./factories/TeamsLogicModuleFactory";
import { TeamsServiceFactory } from "./factories/TeamsServiceFactory";
import { TeamsStoreFactory } from "./factories/TeamsStoreFactory";
import { TeamsWorkerFactory } from "./factories/TeamsWorkerFactory";
import { FluxRegistry } from "@ms/uno-fluxcore/lib/local/registry";
import { UnoRegistryDescriptor } from "../UnoRegistryDescriptor";
import { DynamicImportFailureAction } from "@ms/uno-actions/lib/local/app/DynamicImportFailureAction";
// Host Adaptor
import { FailedReason } from "@ms/uno-hostadaptors/lib/local/IUnoHostAdaptor";
import { TeamsAdaptor } from "@ms/uno-hostadaptors/lib/local/TeamsAdaptor";
// Models
import { InitializationError } from "@ms/uno-errors/lib/local/errors/InitializationError";
import { User } from "@ms/uno-models/lib/local/client/user/User";
import { LoggerManager } from "@ms/uno-telemetry/lib/local/LoggerManager";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { initializeEngagementLogger, setCurrentRouteInfoForLogs } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
import { WebPlatformPerformanceMarker } from "@ms/uno-telemetry/lib/local/performance/WebPlatformPerformanceMarker";
// Utilities
import { logNotificationInfo } from "@ms/uno-routing";
import { getLocalStorage } from "@ms/uno-utilities/lib/local/storage/StorageUtilities";
import { ComponentLoader } from "../utilities/loader/ComponentLoader";
import React from "react";
import * as ReactDOM from "react-dom";
/**
 * The production bootstrap function
 */
export async function bootstrap() {
    // Set up performance Marker and mark bootstrap
    const performanceMarker = new WebPlatformPerformanceMarker();
    performanceMarker.mark(MarkerTag.AppBootstrap);
    // Initialize the host adaptor
    const hostAdaptor = new TeamsAdaptor(performanceMarker);
    // Teams SDK, AppContext needs to be initialized only once. Re-boot for cached load should skip the same.
    if (!hostAdaptor.isInitialized()) {
        await hostAdaptor.initializeSdk();
        try {
            setupAppContext();
        }
        catch (e) {
            const err = new InitializationError(e.name, e.message);
            hostAdaptor.notifyRenderFailure({ reason: FailedReason.Other, message: err.message });
            // explicitly throw error here (as it's not sufficiently handled)
            throw err;
        }
    }
    const configProvider = () => AppContext.appConfiguration;
    const clientFlavor = configProvider().sessionMetaData.appMetadata.clientFlavor;
    hostAdaptor.setConfigProvider(configProvider);
    // Cast to Teams context is safe as we are in Teams
    const context = (await getContextAndInitializeCultureData(hostAdaptor));
    const telemetryContext = getTelemetryContext(context);
    /** Teams client sessionId */
    const teamsSessionId = context.sessionId;
    /** The permanent Teams Ring the user belongs to in ECS */
    const homeRingId = configProvider().sessionMetaData?.homeRingId ?? "";
    /** Teams license type */
    const teamsLicenseType = context.userTeamsLicenseType ?? "";
    // Setup the flux registry
    const registry = new FluxRegistry(UnoRegistryDescriptor);
    // Setup loggers
    const loggerManager = new LoggerManager(telemetryContext, configProvider);
    const telemetryLoggers = loggerManager.getLoggers();
    const loggers = {
        ...telemetryLoggers,
        performanceMarker,
    };
    hostAdaptor.setLoggers(loggers);
    let initialRoute = getInitialRoute(clientFlavor, configProvider);
    const initialRouteFromDeepLink = await hostAdaptor.getInitialRoute();
    if (initialRouteFromDeepLink != null) {
        initialRoute = initialRouteFromDeepLink;
    }
    setCurrentRouteInfoForLogs(initialRoute.subRouteType, initialRoute.subRoute, loggers.logHandler);
    // Logging XSessionId(Current teams sessionId) to correlate
    const { hostname } = location;
    loggers.traceLogger.logTrace(0x1e423004 /* tag_4q9ae */, TraceLevel.Info, `[Bootstrap] Microsoft Teams SDK is initialized and bootstrapping application`, {
        XSessionId: teamsSessionId,
        HomeRingId: homeRingId,
        XLicenseType: teamsLicenseType,
        CorrelationId: configProvider().sessionMetaData.appLoadCorrelationId,
        ServiceBuildVersion: configProvider().sessionMetaData.serverBuild,
        Hostname: hostname,
    });
    const ringIdValidationErrorMessage = hostAdaptor.validateRingId();
    if (ringIdValidationErrorMessage) {
        const err = new InitializationError("InvalidTeamsRingId", ringIdValidationErrorMessage);
        loggers.traceLogger.logTrace(0x1e3d2214 /* tag_4psiu */, TraceLevel.Error, `[ErrorType=${err.name}][Error=${err.message}]`);
        // TODO WI #1234: Evaluate whether to stop here, notifyFailure or re-throw upon config mismatch with host, based on telemetry/testing
    }
    // Check if the combination of rollout stage and environment is unexpected
    const isCombinationUnexpected = AppContext.isRolloutStageAndEnvironmentCombinationUnexpected();
    if (isCombinationUnexpected) {
        loggers.traceLogger.logTrace(0x1e3e26d6 /* tag_4p81w */, TraceLevel.Error, `[Bootstrap] Found invalid combination of rollout stage and environment: [RolloutStage=${configProvider().settings.rolloutStage}][Environment=${configProvider().sessionMetaData.environment}]`);
    }
    // Setup persistent storage
    const persistentStorage = getLocalStorage(context.userId, loggers.traceLogger, configProvider);
    const user = User.fromUserContext(context);
    const loader = new ComponentLoader(loggers);
    const storeFactory = new TeamsStoreFactory(loader, context, user, initialRoute, loggers, persistentStorage, configProvider, initialRouteFromDeepLink != null, null /** No default query parameters **/);
    const storeProviders = getStoreProviders(storeFactory);
    // Initialize engagement logging
    initializeEngagementLogger(storeProviders.route().getCurrentSubRouteType.bind(storeProviders.route()), loggers.engagementLogger);
    // Log relevant engagement info (which can only be done AFTER we've initialized EngagementLogger)
    loggers.engagementLogger.logEngagement(EngagementTelemetryConstants.TeamsApp, EngagementTelemetryConstants.Bootstrap, {
        XSessionId: teamsSessionId,
        HomeRingId: homeRingId,
        XLicenseType: teamsLicenseType,
        Hostname: hostname,
    });
    logNotificationInfo(initialRoute, loggers);
    logPlanViewSubRouteType(EngagementTelemetryConstants.TeamsApp, storeProviders.route().getCurrentSubRouteType(), loggers);
    // Dispatcher for the flux system.
    const dispatcher = getAsyncDispatcher(registry, storeFactory, loggers);
    loader.setOnFailure((name) => dispatcher.dispatchActionAsync(new DynamicImportFailureAction()));
    // Set up request prioritizer
    const requestPrioritizer = getRequestPrioritizer(storeProviders);
    // Setup Providers
    const serviceFactory = new TeamsServiceFactory(loader, loggers);
    // lazy loaded logic modules depend on lazy loaded services typically but authModule as an exception is needed by services upfront.
    // That is why authModule is created here and passed to services.
    const authService = getAuthServiceProvider(serviceFactory, dispatcher, hostAdaptor, loggers, configProvider)();
    const authStore = storeFactory.getAuthStoreProvider()();
    const lmFactory = new TeamsLogicModuleFactory(loader, loggers);
    const authModule = lmFactory.getAuthModuleProvider(dispatcher, authService, authStore, configProvider)();
    // Auth module's fetchAccessTokenAsync method needed by services
    const fetchAccessToken = authModule.fetchAccessTokenAsync.bind(authModule);
    // Auth module's refreshAccessTokenAsync method needed by services
    const refreshAccessToken = authModule.refreshAccessTokenAsync.bind(authModule);
    const serviceOrchestrator = new ProductionServiceOrchestrator(serviceFactory, requestPrioritizer, configProvider, loggers, fetchAccessToken, refreshAccessToken);
    const serviceProviders = getServiceProviders(serviceOrchestrator, authService);
    const lmProviders = getLogicModuleProviders(lmFactory, dispatcher, serviceProviders, storeProviders, configProvider, hostAdaptor, authService, authStore, fetchAccessToken);
    const acFactory = new TeamsActionCreatorFactory(loader, loggers);
    const acProviders = getActionCreatorProviders(acFactory, dispatcher, lmProviders, storeProviders, configProvider, hostAdaptor);
    // Fire app start action (must be done after store init)
    const perfCollectionActionCreator = acProviders.performanceCollection();
    perfCollectionActionCreator.appStart(storeProviders.route().getCurrentPath(), Date.now());
    setupHostCaching(configProvider, hostAdaptor, loggers, acProviders);
    const workerFactory = new TeamsWorkerFactory(loader, loggers);
    const workerProviders = getWorkerProviders(workerFactory, acProviders, storeProviders, configProvider, telemetryLoggers, null);
    loadAndInitializeWorkers(workerProviders, requestPrioritizer, loggers, configProvider);
    if (configProvider().flights.EnableUpsellConversion) {
        if (configProvider().flights.EnableBasicPlanParallelConversion) {
            const planConversionStatusTrackerWorker = await workerProviders.planConversionStatusTrackerWorker();
            planConversionStatusTrackerWorker.init();
        }
        else {
            const basicPlanConversionStatusWorker = await workerProviders.basicPlanConversionTrackerWorkerProvider();
            basicPlanConversionStatusWorker.init();
        }
    }
    // Apply Global styles before any classes or styles are generated.
    applyUnoGlobalStyleOverrides();
    registerThemeChange(hostAdaptor, acProviders);
    const showDevTools = configProvider().sessionMetaData.environment === Environment.Ppe;
    ReactDOM.render(React.createElement(UnoShell, { dispatcherEventManagement: dispatcher, actionCreatorProviders: acProviders, logicModuleProviders: lmProviders, serviceProviders: serviceProviders, storeProviders: storeProviders, workerProviders: workerProviders, hostAdaptor: hostAdaptor, loggers: loggers, onLoad: () => hostAdaptor.notifyLoaded(), onSuccess: () => hostAdaptor.notifyRenderSuccess(), onFail: (message) => hostAdaptor.notifyRenderFailure({ reason: FailedReason.Other, message }), configProvider: configProvider, showDevTools: showDevTools }), document.getElementById(AppRootElementId));
}
/**
 * Setup load and unload handler for app caching
 * @param hostAdaptor The host adaptor
 * @param loggers Loggers
 * @param acProviders Action creator providers
 */
function setupHostCaching(configProvider, hostAdaptor, loggers, acProviders) {
    if (configProvider().settings.disableAppCaching) {
        return;
    }
    const onLoadCallback = async (newClientFlavor, newRoute) => {
        loggers.traceLogger.logTrace(0x1e3d464e /* tag_4puzo */, TraceLevel.Info, `Teams cached app load. [ClientFlavor=${newClientFlavor}]`);
        // Override client flavor
        const currentClientFlavor = configProvider().sessionMetaData.appMetadata.clientFlavor;
        const clientFlavorChanged = newClientFlavor !== currentClientFlavor;
        AppContext.overrideClientFlavor(newClientFlavor);
        if (clientFlavorChanged) {
            // If there's a client flavor update, re-boot the app to ensure the correct client flavor is used across dependencies
            await bootstrap();
        }
        else {
            // If new route is provided within same client flavor (switch between different tabs), navigate to the new route
            if (newRoute != null) {
                logNotificationInfo(newRoute, loggers);
                const routeActionCreator = acProviders.routing();
                routeActionCreator.navigate(newRoute);
            }
            // Update app theme and re-register theme change handler.
            // This is not required when client flavor changes as the app will be re-booted and handle theme inherently
            const newTheme = (await hostAdaptor.getContext()).theme;
            const appContextActionCreator = await acProviders.appContext();
            appContextActionCreator.updateAppTheme(newTheme);
            registerThemeChange(hostAdaptor, acProviders);
            // Call notify success to indicate that the app has been successfully loaded from cache
            hostAdaptor.notifyRenderSuccess();
        }
        loggers.performanceMarker.mark(MarkerTag.CachedLoadComplete, {
            oldFlavor: currentClientFlavor, // Client flavor before the cached load
            newFlavor: newClientFlavor, // Client flavor after the cached load
        });
    };
    hostAdaptor.setupHostCaching(onLoadCallback);
}
/**
 * Register theme change handler to update theme
 * @param hostAdaptor The host adaptor
 * @param acProviders The action creator providers
 */
function registerThemeChange(hostAdaptor, acProviders) {
    // Register theme change handler to update theme
    hostAdaptor.registerThemeChangeHandler(async (theme) => {
        const appContextAc = await acProviders.appContext();
        appContextAc.updateAppTheme(theme);
    });
}
